import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Printer, Repeat, X } from 'lucide-react'
import { useId, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from 'react'
import { dateProblem, REPEATS, type Repeats } from '../../../shared/calendar'
import { ConfirmButton } from '@/components/ConfirmButton'
import { FormError } from '@/components/FormError'
import { Loader } from '@/components/Loader'
import { ShareWith } from '@/components/ShareWith'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import { clockTime, REPEAT_LABELS, useCalendarDate, type CalendarDate } from '@/lib/calendar'
import type { Family, Space } from '@/lib/families'
import { audienceBody, audienceFrom, audienceSaid, startingAudience, type Audience } from '@/lib/spaces'
import { cn } from '@/lib/utils'

// What's open: a date to look at (or change, if it's yours), or a new one starting on a day.
export type Opened = { id: string } | { on: string }

// Adding a date to the Calendar, or opening one: its title, its day or days, all day or at a time, how often it comes
// round, a note, who sees it, and whether printed calendars include it. A date opens to read; your own has Edit.
export function DateSheet({
  opened,
  onClose,
  family,
  space,
}: {
  opened: Opened | null
  onClose: () => void
  family: Family
  space: Space | null
}) {
  const id = opened && 'id' in opened ? opened.id : null
  const found = useCalendarDate(id)
  const date = found.data
  return (
    <Dialog open={!!opened} onOpenChange={(open) => !open && onClose()}>
      {/* Nothing is focused on open, so a phone's keyboard waits until a field is tapped instead of pushing the sheet up. */}
      <DialogContent
        className="max-h-[90svh] overflow-y-auto sm:max-w-lg"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          ;(e.currentTarget as HTMLElement).focus()
        }}
      >
        {opened && 'on' in opened ? (
          <DateForm key={`new:${opened.on}`} family={family} space={space} on={opened.on} onDone={onClose} />
        ) : found.error ? (
          <>
            <DialogHeader>
              <DialogTitle>Date</DialogTitle>
              <DialogDescription className="sr-only">This date couldn’t be opened.</DialogDescription>
            </DialogHeader>
            <FormError error={found.error} />
          </>
        ) : !date ? (
          <>
            <DialogTitle className="sr-only">Date</DialogTitle>
            <DialogDescription className="sr-only">Opening the date</DialogDescription>
            <Loader label="Opening the date" />
          </>
        ) : date.mine ? (
          <MyDate key={date.id} family={family} space={space} date={date} onDone={onClose} />
        ) : (
          <DateView date={date} />
        )}
      </DialogContent>
    </Dialog>
  )
}

// Your own date reads like anyone else's until you choose to change it.
function MyDate({
  family,
  space,
  date,
  onDone,
}: {
  family: Family
  space: Space | null
  date: CalendarDate
  onDone: () => void
}) {
  const [editing, setEditing] = useState(false)
  return editing ? (
    <DateForm family={family} space={space} date={date} onDone={onDone} />
  ) : (
    <DateView date={date} onEdit={() => setEditing(true)} />
  )
}

// A date to read, with Edit when it's yours.
function DateView({ date, onEdit }: { date: CalendarDate; onEdit?: () => void }) {
  return (
    <div className="flex flex-col gap-5">
      <DialogHeader>
        <p className="eyebrow">Added by {onEdit ? 'you' : date.author.name}</p>
        <DialogTitle>{date.title}</DialogTitle>
        <DialogDescription>{whenSaid(date)}</DialogDescription>
      </DialogHeader>
      {date.repeats !== 'none' && (
        <p className="flex items-center gap-2 text-sub text-muted-foreground">
          <Repeat className="size-4" aria-hidden />
          {REPEAT_LABELS[date.repeats]}
          {date.repeatsUntil && `, until ${dayLabel(date.repeatsUntil)}`}
        </p>
      )}
      {date.note && <p className="whitespace-pre-wrap">{date.note}</p>}
      <p className="text-sub text-muted-foreground">Shared with {audienceSaid(date, 'you')}</p>
      {onEdit && !date.onPrints && <p className="text-sub text-muted-foreground">Left off printed calendars</p>}
      {onEdit && (
        <div className="flex justify-end">
          <Button type="button" onClick={onEdit}>
            Edit
          </Button>
        </div>
      )}
    </div>
  )
}

function DateForm({
  family,
  space,
  date,
  on,
  onDone,
}: {
  family: Family
  space: Space | null
  date?: CalendarDate
  on?: string
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const ids = useId()
  const first = date?.startsOn ?? on ?? ''
  const [title, setTitle] = useState(date?.title ?? '')
  const [startsOn, setStartsOn] = useState(first)
  // The last day, the same as the first unless it runs over several, as other calendars show it.
  const [endsOn, setEndsOn] = useState(date?.endsOn ?? first)
  const [allDay, setAllDay] = useState(!date?.startsAt)
  const [startsAt, setStartsAt] = useState(date?.startsAt ?? '09:00')
  const [endsAt, setEndsAt] = useState(date?.endsAt ?? hourAfter(date?.startsAt ?? '09:00'))
  const [repeats, setRepeats] = useState<Repeats>(date?.repeats ?? 'none')
  const [repeatsUntil, setRepeatsUntil] = useState(date?.repeatsUntil ?? '')
  const [note, setNote] = useState(date?.note ?? '')
  const [onPrints, setOnPrints] = useState(date?.onPrints ?? true)
  const [who, setWho] = useState<Audience>(() => (date ? audienceFrom(date) : startingAudience(family, space)))
  const rule = {
    startsOn,
    endsOn: endsOn && endsOn !== startsOn ? endsOn : null,
    startsAt: allDay ? null : startsAt || null,
    endsAt: allDay || !startsAt ? null : endsAt || null,
    repeats,
    repeatsUntil: repeats === 'none' ? null : repeatsUntil || null,
  }
  const [shown, setShown] = useState<string | null>(null)

  // Moving the first day carries the last one with it, so it never ends before it starts.
  const moveStart = (day: string) => {
    if (day && startsOn && endsOn >= startsOn) setEndsOn(shiftDay(endsOn, daysBetween(startsOn, day)))
    else setEndsOn(day)
    setStartsOn(day)
  }
  const moveStartTime = (time: string) => {
    if (time && (!endsAt || endsAt <= time)) setEndsAt(hourAfter(time))
    setStartsAt(time)
  }

  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: ['calendar'] })
    await queryClient.invalidateQueries({ queryKey: ['calendar-date'] })
    onDone()
  }
  const save = useMutation({
    mutationFn: () => {
      const body = { title: title.trim(), ...rule, note: note.trim(), onPrints, ...audienceBody(who) }
      return date
        ? api(`/calendar/dates/${date.id}`, { method: 'PATCH', body })
        : api(`/families/${family.id}/calendar/dates`, { method: 'POST', body })
    },
    onSuccess: done,
  })
  const remove = useMutation({
    mutationFn: () => api(`/calendar/dates/${date!.id}`, { method: 'DELETE' }),
    onSuccess: done,
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!allDay && !startsAt) return setShown('Give the time it starts, or make it all day.')
    const problem = dateProblem(rule)
    setShown(problem)
    if (!problem) save.mutate()
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <DialogHeader>
        <DialogTitle>{date ? 'Change this date' : 'Add a date'}</DialogTitle>
        <DialogDescription className="sr-only">
          {date ? whenSaid(date) : 'A day worth remembering, once or every time it comes round.'}
        </DialogDescription>
      </DialogHeader>

      <Input
        aria-label="What’s the day?"
        required
        maxLength={120}
        placeholder="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="h-12 text-base"
      />

      <Group>
        <Row label="All day" htmlFor={`${ids}-all-day`}>
          <Switch id={`${ids}-all-day`} checked={allDay} onChange={setAllDay} />
        </Row>
        <Row label="Starts" htmlFor={`${ids}-starts`}>
          <Chip
            id={`${ids}-starts`}
            aria-label="Starts on"
            type="date"
            required
            value={startsOn}
            onChange={moveStart}
          />
          {!allDay && <Chip aria-label="Starts at" type="time" required value={startsAt} onChange={moveStartTime} />}
        </Row>
        <Row label="Ends" htmlFor={`${ids}-ends`}>
          <Chip
            id={`${ids}-ends`}
            aria-label="Ends on"
            type="date"
            min={startsOn || undefined}
            value={endsOn}
            onChange={setEndsOn}
          />
          {!allDay && <Chip aria-label="Ends at" type="time" value={endsAt} onChange={setEndsAt} />}
        </Row>
      </Group>

      <Group>
        <div className="grid gap-2.5 px-4 py-3">
          <p id={`${ids}-repeats`} className="text-sm font-medium">
            Repeats
          </p>
          <Segmented
            labelledBy={`${ids}-repeats`}
            value={repeats}
            options={REPEATS.map((x) => [x, REPEAT_SHORT[x]])}
            onChange={setRepeats}
          />
        </div>
        {repeats !== 'none' && (
          <Row label="Until" htmlFor={`${ids}-until`}>
            {repeatsUntil ? (
              <>
                <Chip
                  id={`${ids}-until`}
                  aria-label="Repeat until"
                  type="date"
                  min={startsOn || undefined}
                  value={repeatsUntil}
                  onChange={setRepeatsUntil}
                />
                <button
                  type="button"
                  aria-label="Repeat forever"
                  onClick={() => setRepeatsUntil('')}
                  className="grid size-9 place-items-center rounded-full text-muted-foreground hover:bg-background"
                >
                  <X className="size-4" aria-hidden />
                </button>
              </>
            ) : (
              <button
                id={`${ids}-until`}
                type="button"
                onClick={() => setRepeatsUntil(shiftDay(startsOn, 365))}
                className="h-9 rounded-lg bg-background px-3 text-sm font-medium"
              >
                Forever
              </button>
            )}
          </Row>
        )}
      </Group>

      <Textarea
        aria-label="Note"
        maxLength={2000}
        placeholder="Note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <section aria-labelledby={`${ids}-who`} className="grid gap-3">
        <h3 id={`${ids}-who`} className="text-sm font-medium">
          Who sees it?
        </h3>
        <ShareWith here={family.id} value={who} onChange={setWho} onlyMeHint="Just you, on your calendar" />
      </section>

      <Group>
        <Row
          label={
            <span className="flex items-center gap-2">
              <Printer className="size-4" aria-hidden />
              Show on prints
            </span>
          }
          hint="Printed calendars include this date."
          htmlFor={`${ids}-prints`}
        >
          <Switch id={`${ids}-prints`} checked={onPrints} onChange={setOnPrints} />
        </Row>
      </Group>

      <FormError error={shown ?? save.error ?? remove.error} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        {date ? (
          <ConfirmButton
            label="Delete"
            title="Delete this date?"
            description={
              date.repeats === 'none'
                ? 'It comes off the calendar for everyone who sees it.'
                : 'Every time it comes round comes off the calendar, for everyone who sees it.'
            }
            confirm="Delete"
            disabled={remove.isPending}
            onConfirm={() => remove.mutate()}
          />
        ) : (
          <span />
        )}
        <Button type="submit" disabled={save.isPending}>
          {date ? 'Save' : 'Add the date'}
        </Button>
      </div>
    </form>
  )
}

const REPEAT_SHORT: Record<Repeats, string> = {
  none: 'Never',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
}

const dayMs = 86_400_000
const shiftDay = (day: string, by: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + by * dayMs).toISOString().slice(0, 10)
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / dayMs)
// An hour on, as other calendars start an end time, kept within the day.
function hourAfter(time: string) {
  const [h, m] = time.split(':').map(Number) as [number, number]
  return h >= 23 ? '23:59' : `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// Rows grouped on one card, as a phone's settings and calendar apps show them.
function Group({ children }: { children: ReactNode }) {
  return <div className="flex flex-col divide-y divide-border/70 rounded-2xl bg-secondary/60">{children}</div>
}

function Row({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: ReactNode
  hint?: string
  htmlFor: string
  children: ReactNode
}) {
  return (
    <div className="flex min-h-13 items-center justify-between gap-3 px-4 py-2">
      <span className="grid shrink-0 gap-0.5">
        <label htmlFor={htmlFor} className="text-sm font-medium">
          {label}
        </label>
        {hint && <span className="text-sub text-muted-foreground">{hint}</span>}
      </span>
      {/* A date and a time side by side wrap onto two lines when the screen is too narrow for both. */}
      <span className="flex min-w-0 flex-wrap items-center justify-end gap-2">{children}</span>
    </div>
  )
}

// A date or time as a small chip that opens the phone's own picker.
function Chip({
  onChange,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & { onChange: (value: string) => void }) {
  return (
    <input
      {...props}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 max-w-full min-w-0 appearance-none rounded-lg bg-background px-3 text-sm font-medium tabular-nums outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
    />
  )
}

// One choice from a few, side by side in one control.
function Segmented<T extends string>({
  labelledBy,
  value,
  options,
  onChange,
}: {
  labelledBy: string
  value: T
  options: [T, string][]
  onChange: (value: T) => void
}) {
  return (
    <div
      role="group"
      aria-labelledby={labelledBy}
      className="grid gap-0.5 rounded-xl bg-background p-0.5"
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map(([option, name]) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className="min-h-9 rounded-[0.6rem] px-1 text-sm font-medium text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-pressed:bg-brand aria-pressed:text-brand-foreground pointer-coarse:min-h-10"
        >
          {name}
        </button>
      ))}
    </div>
  )
}

// An on/off switch.
function Switch({ id, checked, onChange }: { id: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <input
      id={id}
      type="checkbox"
      role="switch"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className={cn(
        'relative h-7 w-12 shrink-0 cursor-pointer appearance-none rounded-full bg-input transition-colors outline-none',
        'before:absolute before:top-0.5 before:left-0.5 before:size-6 before:rounded-full before:bg-background before:shadow-sm before:transition-transform',
        'checked:bg-brand checked:before:translate-x-5 focus-visible:ring-[3px] focus-visible:ring-ring/50',
      )}
    />
  )
}

const dayLabel = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })

// "Sat, October 24, 2026 · 6:00 PM – 9:00 PM", or the first and last days of one that runs over several.
function whenSaid(date: CalendarDate) {
  const days = date.endsOn ? `${dayLabel(date.startsOn)} to ${dayLabel(date.endsOn)}` : dayLabel(date.startsOn)
  if (!date.startsAt) return `${days} · All day`
  return `${days} · ${clockTime(date.startsAt)}${date.endsAt ? ` – ${clockTime(date.endsAt)}` : ''}`
}
